import { VisuallyHidden } from "@mantine/core";
import { Image, Video } from "lucide-react";
import { useState } from "react";

import type { TemplateCard as Template } from "../../api/generated/model";
import { CoverVideo } from "../../ui/CoverVideo";
import classes from "./templates.module.css";
import { TierBolts } from "./TemplateCost";

export interface TemplateCardProps {
  template: Template;
  /** Opens the template's preview. A Coming soon card opens nothing. */
  onOpen: () => void;
}

/**
 * Live's template card (`template-card`, captures 01, 02 and 43): one 3:4 button named after the
 * template, its cover (a muted looping video for a video template, its first frame until it
 * plays and loaded only on screen, #828 bug 17; else a picture; a grey tile with the type's icon
 * until the covers are imported), the title over a scrim, and the type's
 * frosted badge at the top left, its tier bolts at the top right (G14-template-costs). It grows on hover and on keyboard focus. One of live's Coming
 * soon cards wears live's pill instead of the badge and is disabled, as live's: it opens nothing.
 */
export function TemplateCard({ template, onOpen }: TemplateCardProps) {
  const cover = template.cover_url;
  // The scrim and the title wait for the cover, as live's.
  const [loaded, setLoaded] = useState(cover === null);
  const video = template.media === "video";
  const Icon = video ? Video : Image;
  return (
    <button
      type="button"
      className={classes.card}
      aria-label={template.title}
      disabled={template.coming_soon}
      data-loading={!loaded || undefined}
      onClick={onOpen}
    >
      {cover === null ? (
        <span className={classes.fallback}>
          <Icon size={32} strokeWidth={1.5} />
        </span>
      ) : video ? (
        // Ready once its first frame shows (with no poster, once its size is known, as live's). It
        // plays the card's lighter rendition where there is one (#828 bug 17), else live's file.
        <CoverVideo
          className={classes.cover}
          src={template.cover_card_url ?? cover}
          poster={template.cover_poster_url ?? null}
          onReady={() => setLoaded(true)}
        />
      ) : (
        <img
          className={classes.cover}
          src={cover}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      )}
      <span className={classes.scrim} />
      <span className={classes.content}>
        <span className={classes.cardTop}>
          {template.coming_soon ? (
            <span className={classes.soonPill}>Coming soon</span>
          ) : (
            <span className={classes.badge}>
              <Icon size={14} aria-hidden />
              <VisuallyHidden>{video ? "Video template" : "Image template"}</VisuallyHidden>
            </span>
          )}
          {/* Live's tier bolts, from its declared cost of one output; none without one. */}
          {template.estimate === null || template.estimate === undefined || template.coming_soon ? null : (
            <TierBolts estimate={template.estimate} />
          )}
        </span>
        <span className={classes.title}>{template.title}</span>
      </span>
    </button>
  );
}
